import React, { createContext, ReactElement, useContext, useReducer } from 'react';
import Layout, { LayoutType } from 'domain/entity/layout';

export enum LAYOUT_STATUS {
    READY = 'READY',
    FORCE_RESET = 'FORCE_RESET',
}

export enum ACTION_TYPE {
    EDIT_LAYOUT_MODE = 'EDIT_LAYOUT_MODE',
    RESET = 'RESET',
    HAS_LAYOUT_CHANGES = 'HAS_LAYOUT_CHANGES',
    SET_LAYOUTS = 'SET_LAYOUTS',
    SELECT_LAYOUT_TYPE = 'SELECT_LAYOUT_TYPE',
    SET_LAYOUT_STATUS = 'SET_LAYOUT_STATUS',
}

export const initialState: LayoutState = {
    editLayoutMode: false,
    hasLayoutChanges: false,
    layouts: new Map(),
    selectedLayoutType: LayoutType.DESKTOP,
    layoutStatus: LAYOUT_STATUS.READY,
};

type SetEditLayoutMode = {
    type: ACTION_TYPE.EDIT_LAYOUT_MODE;
    payload: boolean;
};

type SelectLayout = {
    type: ACTION_TYPE.SELECT_LAYOUT_TYPE;
    payload: LayoutType;
};

type Reset = {
    type: ACTION_TYPE.RESET;
    payload: null;
};

// LAYOUT CONTEXT PROPS
type SetHasLayoutChanges = {
    type: ACTION_TYPE.HAS_LAYOUT_CHANGES;
    payload: boolean;
};

type SetLayouts = {
    type: ACTION_TYPE.SET_LAYOUTS;
    payload: Map<LayoutType, Layout>;
};

type SetLayoutStatus = {
    type: ACTION_TYPE.SET_LAYOUT_STATUS;
    payload: LAYOUT_STATUS;
};

export type LayoutState = {
    editLayoutMode: boolean;
    hasLayoutChanges: boolean;
    layouts: Map<LayoutType, Layout>;
    selectedLayoutType: LayoutType;
    layoutStatus: LAYOUT_STATUS;
};

export type LayoutsViewDispatch = {
    setEditLayoutMode: (editLayoutMode: boolean) => void;
    setHasLayoutChanges: (hasLayoutChanges: boolean) => void;
    setLayouts: (layouts: Map<LayoutType, Layout>) => void;
    selectLayoutType: (layoutType: LayoutType) => void;
    setLayoutStatus: (status: LAYOUT_STATUS) => void;
    reset: () => void;
};

type LayoutsAction = SetEditLayoutMode | SetHasLayoutChanges | SetLayouts | SelectLayout | SetLayoutStatus | Reset;

export const useCaseLayoutReducer = (
    state: LayoutState,
    action: LayoutsAction
    // eslint-disable-next-line consistent-return
): LayoutState => {
    const { type, payload } = action;
    // eslint-disable-next-line default-case
    switch (type) {
        case ACTION_TYPE.EDIT_LAYOUT_MODE: {
            return {
                ...state,
                editLayoutMode: payload,
            };
        }

        case ACTION_TYPE.RESET: {
            return { ...initialState };
        }

        case ACTION_TYPE.HAS_LAYOUT_CHANGES: {
            return { ...state, hasLayoutChanges: payload };
        }

        case ACTION_TYPE.SET_LAYOUTS: {
            return { ...state, layouts: payload };
        }

        case ACTION_TYPE.SELECT_LAYOUT_TYPE: {
            return {
                ...state,
                selectedLayoutType: payload,
            };
        }

        case ACTION_TYPE.SET_LAYOUT_STATUS: {
            return {
                ...state,
                layoutStatus: payload,
            };
        }
    }
};

type DashboardEditProviderType = {
    children: ReactElement;
};

export const LayoutsViewDispatchContext = createContext({} as LayoutsViewDispatch);
export const LayoutsViewStateContext = createContext(initialState);

const dispatchLayoutsView = (): LayoutsViewDispatch => useContext(LayoutsViewDispatchContext);
const useLayoutsView = (): LayoutState => useContext(LayoutsViewStateContext);

const LayoutsViewProvider = (props: DashboardEditProviderType): ReactElement => {
    const { children } = props;
    const [state, dispatch] = useReducer(useCaseLayoutReducer, initialState);

    const setEditLayoutMode = (editLayoutMode: boolean) =>
        dispatch({
            type: ACTION_TYPE.EDIT_LAYOUT_MODE,
            payload: editLayoutMode,
        });

    const reset = () =>
        dispatch({
            type: ACTION_TYPE.RESET,
            payload: null,
        });

    const setHasLayoutChanges = (hasLayoutChanges: boolean) =>
        dispatch({
            type: ACTION_TYPE.HAS_LAYOUT_CHANGES,
            payload: hasLayoutChanges,
        });

    const setLayouts = (layouts: Map<LayoutType, Layout>) =>
        dispatch({
            type: ACTION_TYPE.SET_LAYOUTS,
            payload: layouts,
        });

    const selectLayoutType = (layoutType: LayoutType) =>
        dispatch({
            type: ACTION_TYPE.SELECT_LAYOUT_TYPE,
            payload: layoutType,
        });

    const setLayoutStatus = (layoutStatus: LAYOUT_STATUS) =>
        dispatch({
            type: ACTION_TYPE.SET_LAYOUT_STATUS,
            payload: layoutStatus,
        });

    return (
        <LayoutsViewStateContext.Provider value={state}>
            <LayoutsViewDispatchContext.Provider
                value={{
                    setEditLayoutMode,
                    reset,
                    setHasLayoutChanges,
                    setLayouts,
                    selectLayoutType,
                    setLayoutStatus,
                }}
            >
                {children}
            </LayoutsViewDispatchContext.Provider>
        </LayoutsViewStateContext.Provider>
    );
};

export { LayoutsViewProvider, dispatchLayoutsView, useLayoutsView };
